import React, { useMemo, useState, useCallback } from 'react';
import type {
    LayoutConfig,
    LayoutContainer as LayoutContainerType,
    ItemLayout,
    ResponsiveBreakpoints,
    ResponsiveLayout,
    ContentItem,
} from '../schema';
import { LayoutContainer, DEFAULT_VERTICAL_LAYOUT } from './container';
import { LayoutItem } from './item';
import {
    getCurrentBreakpoint,
    resolveResponsiveLayout,
    DEFAULT_BREAKPOINTS,
    type BreakpointName,
} from './responsive';
import { cn } from '../lib/utils';

// ============================================
// Layout Engine Props
// ============================================

export interface LayoutEngineProps {
    /** Content items to render */
    items: ContentItem[];
    /** Layout configuration */
    layout?: LayoutConfig | LayoutContainerType;
    /** Responsive layout configuration */
    responsiveLayout?: ResponsiveLayout;
    /** Custom breakpoints */
    breakpoints?: ResponsiveBreakpoints;
    /** Item layout overrides by item ID */
    itemLayouts?: Record<string, ItemLayout>;
    /** Render function for each item */
    renderItem: (item: ContentItem, index: number) => React.ReactNode;
    /** Callback when an item is clicked */
    onItemClick?: (item: ContentItem, index: number) => void;
    /** Currently active item ID */
    activeItemId?: string;
    /** Disabled item IDs */
    disabledItemIds?: string[];
    /** Additional CSS class for the engine container */
    className?: string;
    /** Additional inline styles */
    style?: React.CSSProperties;
    /** Current window width (for SSR or controlled mode) */
    width?: number;
}

// ============================================
// Layout Engine Component
// ============================================

/**
 * Main layout engine that renders content items according to layout configuration
 */
export function LayoutEngine({
    items,
    layout,
    responsiveLayout,
    breakpoints = DEFAULT_BREAKPOINTS,
    itemLayouts = {},
    renderItem,
    onItemClick,
    activeItemId,
    disabledItemIds = [],
    className,
    style,
    width,
}: LayoutEngineProps): React.ReactElement {
    // Track window width for responsive layouts
    const [windowWidth, setWindowWidth] = useState(() => {
        if (width !== undefined) return width;
        if (typeof window !== 'undefined') return window.innerWidth;
        return DEFAULT_BREAKPOINTS.desktop;
    });

    // Update width on resize
    React.useEffect(() => {
        if (width !== undefined) {
            setWindowWidth(width);
            return;
        }

        if (typeof window === 'undefined') return;

        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [width]);

    // Resolve the effective layout configuration
    const effectiveLayout = useMemo((): LayoutConfig => {
        // If responsive layout is provided, resolve based on width
        if (responsiveLayout) {
            return resolveResponsiveLayout(responsiveLayout, windowWidth, breakpoints);
        }

        // If layout container is provided, use its layout
        if (layout && 'children' in layout) {
            return (layout as LayoutContainerType).layout;
        }

        // If simple layout config is provided, use it
        if (layout) {
            return layout as LayoutConfig;
        }

        // Default to vertical layout
        return DEFAULT_VERTICAL_LAYOUT;
    }, [layout, responsiveLayout, windowWidth, breakpoints]);

    // Current breakpoint for context
    const currentBreakpoint = useMemo(
        () => getCurrentBreakpoint(windowWidth, breakpoints),
        [windowWidth, breakpoints]
    );

    // Handle item click
    const handleItemClick = useCallback(
        (item: ContentItem, index: number) => {
            onItemClick?.(item, index);
        },
        [onItemClick]
    );

    // Render items based on layout type
    const renderedContent = useMemo(() => {
        // Check if we have a container tree
        if (layout && 'children' in layout) {
            return renderContainerTree(
                layout as LayoutContainerType,
                items,
                itemLayouts,
                renderItem,
                handleItemClick,
                activeItemId,
                disabledItemIds
            );
        }

        // Render flat list of items
        return items.map((item, index) => {
            const itemLayout = itemLayouts[item.id];
            const isActive = item.id === activeItemId;
            const isDisabled = disabledItemIds.includes(item.id);

            return (
                <LayoutItem
                    key={item.id}
                    id={item.id}
                    layout={itemLayout}
                    isActive={isActive}
                    isDisabled={isDisabled}
                    onClick={onItemClick ? () => handleItemClick(item, index) : undefined}
                >
                    {renderItem(item, index)}
                </LayoutItem>
            );
        });
    }, [
        layout,
        items,
        itemLayouts,
        renderItem,
        handleItemClick,
        activeItemId,
        disabledItemIds,
        onItemClick,
    ]);

    return (
        <div
            className={cn('layout-engine', className)}
            style={style}
            data-breakpoint={currentBreakpoint}
        >
            <LayoutContainer layout={effectiveLayout}>{renderedContent}</LayoutContainer>
        </div>
    );
}

// ============================================
// Container Tree Renderer
// ============================================

/**
 * Recursively render a container tree
 */
function renderContainerTree(
    container: LayoutContainerType,
    items: ContentItem[],
    itemLayouts: Record<string, ItemLayout>,
    renderItem: (item: ContentItem, index: number) => React.ReactNode,
    onItemClick: (item: ContentItem, index: number) => void,
    activeItemId?: string,
    disabledItemIds: string[] = []
): React.ReactElement {
    // Create a map of items by ID for quick lookup
    const itemMap = new Map(items.map((item, index) => [item.id, { item, index }]));

    return (
        <LayoutContainer
            id={container.id}
            layout={container.layout}
            className={container.className}
            style={container.style}
        >
            {container.children.map((child, childIndex) => {
                // If child is a string, it's an item ID
                if (typeof child === 'string') {
                    const entry = itemMap.get(child);
                    if (!entry) return null;

                    const { item, index } = entry;
                    const itemLayout = itemLayouts[item.id];
                    const isActive = item.id === activeItemId;
                    const isDisabled = disabledItemIds.includes(item.id);

                    return (
                        <LayoutItem
                            key={item.id}
                            id={item.id}
                            layout={itemLayout}
                            isActive={isActive}
                            isDisabled={isDisabled}
                            onClick={() => onItemClick(item, index)}
                        >
                            {renderItem(item, index)}
                        </LayoutItem>
                    );
                }

                // If child is a container, recurse
                return (
                    <React.Fragment key={child.id || `container-${childIndex}`}>
                        {renderContainerTree(
                            child,
                            items,
                            itemLayouts,
                            renderItem,
                            onItemClick,
                            activeItemId,
                            disabledItemIds
                        )}
                    </React.Fragment>
                );
            })}
        </LayoutContainer>
    );
}

// ============================================
// Layout Engine Context
// ============================================

export interface LayoutEngineContextValue {
    layout: LayoutConfig;
    breakpoint: BreakpointName;
    width: number;
    itemLayouts: Record<string, ItemLayout>;
}

const LayoutEngineContext = React.createContext<LayoutEngineContextValue | null>(null);

/**
 * Provider for layout engine context
 */
export function LayoutEngineProvider({
    children,
    value,
}: {
    children: React.ReactNode;
    value: LayoutEngineContextValue;
}): React.ReactElement {
    return <LayoutEngineContext.Provider value={value}>{children}</LayoutEngineContext.Provider>;
}

/**
 * Hook to access layout engine context
 */
export function useLayoutEngine(): LayoutEngineContextValue | null {
    return React.useContext(LayoutEngineContext);
}

// ============================================
// Specialized Layout Components
// ============================================

/**
 * Carousel layout for horizontal scrolling content
 */
export interface CarouselLayoutProps extends Omit<LayoutEngineProps, 'layout'> {
    /** Auto-scroll interval in ms (0 to disable) */
    autoScrollInterval?: number;
    /** Show navigation arrows */
    showArrows?: boolean;
    /** Show dot indicators */
    showDots?: boolean;
    /** Translated aria-label for previous button */
    ariaLabelPrevious?: string;
    /** Translated aria-label for next button */
    ariaLabelNext?: string;
}

export function CarouselLayout({
    autoScrollInterval = 0,
    showArrows = true,
    showDots = true,
    ariaLabelPrevious = 'Previous',
    ariaLabelNext = 'Next',
    ...props
}: CarouselLayoutProps): React.ReactElement {
    const [currentIndex, setCurrentIndex] = useState(0);
    const containerRef = React.useRef<HTMLDivElement>(null);

    // Auto-scroll
    React.useEffect(() => {
        if (autoScrollInterval <= 0 || props.items.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % props.items.length);
        }, autoScrollInterval);

        return () => clearInterval(interval);
    }, [autoScrollInterval, props.items.length]);

    // Scroll to current index
    React.useEffect(() => {
        if (!containerRef.current) return;
        const container = containerRef.current.querySelector('.layout-container');
        if (!container) return;

        const items = container.children;
        if (items[currentIndex]) {
            (items[currentIndex] as HTMLElement).scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start',
            });
        }
    }, [currentIndex]);

    const goToPrev = () => {
        setCurrentIndex((prev) => (prev - 1 + props.items.length) % props.items.length);
    };

    const goToNext = () => {
        setCurrentIndex((prev) => (prev + 1) % props.items.length);
    };

    return (
        <div ref={containerRef} className="carousel-layout relative">
            <LayoutEngine
                {...props}
                layout={{ type: 'carousel', gap: '1rem' }}
                activeItemId={props.items[currentIndex]?.id}
            />
            {showArrows && props.items.length > 1 && (
                <>
                    <button
                        onClick={goToPrev}
                        className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full shadow"
                        aria-label={ariaLabelPrevious}
                    >
                        ←
                    </button>
                    <button
                        onClick={goToNext}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full shadow"
                        aria-label={ariaLabelNext}
                    >
                        →
                    </button>
                </>
            )}
            {showDots && props.items.length > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                    {props.items.map((item, index) => (
                        <button
                            key={item.id}
                            onClick={() => setCurrentIndex(index)}
                            className={cn(
                                'w-2 h-2 rounded-full transition-colors',
                                index === currentIndex ? 'bg-primary' : 'bg-gray-300'
                            )}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * Tabs layout for tabbed content
 */
export interface TabsLayoutProps extends Omit<LayoutEngineProps, 'layout'> {
    /** Tab labels (defaults to item titles) */
    tabLabels?: Record<string, string>;
    /** Tab position */
    tabPosition?: 'top' | 'bottom' | 'left' | 'right';
}

export function TabsLayout({
    tabLabels = {},
    tabPosition = 'top',
    ...props
}: TabsLayoutProps): React.ReactElement {
    const [activeTab, setActiveTab] = useState(props.items[0]?.id);
    const activeItem = props.items.find((item) => item.id === activeTab);
    const activeIndex = props.items.findIndex((item) => item.id === activeTab);

    const isVertical = tabPosition === 'left' || tabPosition === 'right';

    const tabs = (
        <div
            className={cn(
                'flex gap-1',
                isVertical ? 'flex-col' : 'flex-row',
                tabPosition === 'bottom' && 'order-2',
                tabPosition === 'right' && 'order-2'
            )}
            role="tablist"
        >
            {props.items.map((item) => (
                <button
                    key={item.id}
                    role="tab"
                    aria-selected={item.id === activeTab}
                    onClick={() => setActiveTab(item.id)}
                    className={cn(
                        'px-4 py-2 border-b-2 transition-colors',
                        item.id === activeTab
                            ? 'border-primary text-primary'
                            : 'border-transparent text-gray-600 hover:text-gray-800'
                    )}
                >
                    {tabLabels[item.id] || `Tab ${props.items.indexOf(item) + 1}`}
                </button>
            ))}
        </div>
    );

    return (
        <div className={cn('tabs-layout', isVertical ? 'flex flex-row' : 'flex flex-col')}>
            {tabs}
            <div className="tab-content flex-1" role="tabpanel">
                {activeItem && props.renderItem(activeItem, activeIndex)}
            </div>
        </div>
    );
}
